import React from 'react'
import { MdDriveFileMove, MdOutlineSelectAll } from "react-icons/md"
import { RiDeleteBin2Fill } from "react-icons/ri"
import { GiCancel } from "react-icons/gi"
import { getButtonClasses, getButtonStyles } from '../../../constants'
import { useTranslate } from '../../../hooks/useTranslate'

interface SelectionControlsProps {
    selectedCount: number
    totalCount?: number  // Total number of selectable items
    onBulkMove?: () => void
    onBulkDelete?: () => void
    onSelectAll?: () => void
    onClearSelection?: () => void
}

const SelectionControls: React.FC<SelectionControlsProps> = ({
    selectedCount,
    totalCount = 0,
    onBulkMove,
    onBulkDelete,
    onSelectAll,
    onClearSelection
}) => {
    const { t } = useTranslate();
    // Check if all available items are selected
    const allSelected = totalCount > 0 && selectedCount === totalCount;
    return (
        <div className="flex flex-col sm:flex-row gap-4 mb-6 justify-end">
            {selectedCount > 0 && onBulkMove && (
                <button
                    type="button"
                    onClick={onBulkMove}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                        background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                        boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
                        e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(220, 38, 38, 0.6), 0 12px 15px -6px rgba(220, 38, 38, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)';
                    }}
                >
                    <MdDriveFileMove className="mr-2 w-5 h-5 text-white" />
                    {t('Move')} <span className="notranslate">{selectedCount}</span> {t('item(s) to folder')}
                </button>
            )}

            {selectedCount > 0 && onBulkDelete && (
                <button
                    type="button"
                    onClick={onBulkDelete}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                        background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                        boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
                        e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(220, 38, 38, 0.6), 0 12px 15px -6px rgba(220, 38, 38, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)';
                    }}
                >
                    <RiDeleteBin2Fill className="mr-2 w-5 h-5 text-white" />
                    {t('Delete Item')} <span className="notranslate">{selectedCount}</span> {t('item(s)')}
                </button>
            )}

            {onSelectAll && (
                <button
                    type="button"
                    onClick={onSelectAll}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                        background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                        boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
                        e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(220, 38, 38, 0.6), 0 12px 15px -6px rgba(220, 38, 38, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)';
                    }}
                >
                    <MdOutlineSelectAll className="mr-2 w-5 h-5 text-white" />
                    {allSelected ? t('Deselect All') : t('Select All')}
                </button>
            )}

            {onClearSelection && (
                <button
                    type="button"
                    onClick={onClearSelection}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                        background: 'linear-gradient(to right, #6b7280, #4b5563, #374151)',
                        boxShadow: '0 10px 25px -5px rgba(107, 114, 128, 0.4), 0 8px 10px -6px rgba(107, 114, 128, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
                        e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(107, 114, 128, 0.6), 0 12px 15px -6px rgba(107, 114, 128, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(107, 114, 128, 0.4), 0 8px 10px -6px rgba(107, 114, 128, 0.3)';
                    }}
                >
                    <GiCancel className="mr-2 w-5 h-5 text-white" />
                    {t('Cancel')}
                </button>
            )}
        </div>
    )
}

export default SelectionControls